import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CanDeactivateFn, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainHeadingComponent } from '../../atoms/main-heading/main-heading.component';
import { StoreAdministrationService } from '../../../services/store/store-administration.service';
import { StoreAdministrationDto, StoreAdministrationStatus } from '../../../services/store/store-administration.types';
import { StoreCreateDialogComponent } from '../../molecules/store-create-dialog/store-create-dialog.component';

@Component({
  selector: 'app-manage-stores', standalone: true, templateUrl: './manage-stores.component.html', styleUrls: ['./manage-stores.component.css'],
  imports: [CommonModule, RouterLink, MainHeadingComponent, MatTableModule, MatPaginatorModule, MatSortModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatDialogModule, MatProgressSpinnerModule],
})
export class ManageStoresComponent implements OnInit, AfterViewInit {
  readonly displayedColumns = ['id', 'place', 'district', 'status', 'origin', 'signature', 'actions'];
  readonly stores = new MatTableDataSource<StoreAdministrationDto>([]);
  readonly dialog = inject(MatDialog);
  status: StoreAdministrationStatus = 'active';
  loading = false;
  error = false;
  private reloadGeneration = 0;
  private createDialog?: MatDialogRef<StoreCreateDialogComponent, boolean>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  constructor(private administration: StoreAdministrationService, private cdr: ChangeDetectorRef) {
    const defaultSortingDataAccessor = this.stores.sortingDataAccessor;
    this.stores.sortingDataAccessor = (store, column) => column === 'status'
      ? Number(store.isActive)
      : defaultSortingDataAccessor(store, column);
    this.stores.filterPredicate = (store, filter) => `${store.id} ${store.place} ${store.district} ${store.signature} ${store.origin}`.toLowerCase().includes(filter);
  }
  ngOnInit(): void { void this.reload(); }
  ngAfterViewInit(): void { this.stores.paginator = this.paginator; this.stores.sort = this.sort; }
  async reload(): Promise<void> {
    const generation = ++this.reloadGeneration;
    const status = this.status;
    this.loading = true; this.error = false;
    this.cdr.markForCheck();
    try {
      const data = await firstValueFrom(this.administration.list(status));
      if (generation !== this.reloadGeneration) return;
      this.stores.data = data.sort((a, b) => a.place.localeCompare(b.place, 'da'));
      this.paginator?.firstPage();
    } catch { if (generation === this.reloadGeneration) this.error = true; }
    finally {
      if (generation === this.reloadGeneration) { this.loading = false; this.cdr.markForCheck(); }
    }
  }
  filter(value: string): void { this.stores.filter = value.trim().toLocaleLowerCase('da'); this.paginator?.firstPage(); }
  async createStore(): Promise<void> {
    if (this.createDialog) return;
    const ref = this.dialog.open<StoreCreateDialogComponent, void, boolean>(StoreCreateDialogComponent, { width: '480px', closeOnNavigation: false });
    this.createDialog = ref;
    const created = await firstValueFrom(ref.afterClosed());
    if (this.createDialog === ref) this.createDialog = undefined;
    if (created) await this.reload();
  }
  canLeave(): boolean {
    const ref = this.createDialog;
    if (!ref) return true;
    if (!ref.componentInstance.canClose()) return false;
    this.createDialog = undefined;
    ref.close();
    return true;
  }
}

export const manageStoresCanDeactivate: CanDeactivateFn<ManageStoresComponent> = component => component.canLeave();
